import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import client, { signUpload } from "@culturehq/client";
import { Button, DateTimeField, EmailField, Form, ImageField, ModalDialog, SelectField, StringField, SubmitButton } from "@culturehq/components";

import { useProfile, useUserSelectOptions } from "app/context";
import { useFieldsLabels } from "app/hooks";
import { getProfilePath } from "app/links";
import { canViewCompaniesField } from "app/permissions";
import { t, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";
import { roles } from "./users/UserEditModal";

const FLAG_OPTIONS = [
  { value: true, label: t("Yes") },
  { value: false, label: t("No") }
];

class InviteModalContext extends Component {
  state = { error: false,
    imageProgress: null,
    open: false,
    redirectTo: null,
    sendInvitation: true };

  handleModalOpen = () => {
    this.setState({ open: true, error: false });
  };

  handleModalClose = () => {
    this.setState({ open: false, error: false });
  };

  handleSubmit = async ({ avatar, ...others }) => {
    this.setState({ error: false });

    try {
      const values = others;
      if (avatar && avatar.size) {
        values.avatarUrl = await signUpload(avatar, imageProgress => {
          this.setState({ imageProgress });
        });
      }
      const { invite } = await client.sendInvite(values);
      this.setState({ open: false, redirectTo: getProfilePath(invite.user) });
    } catch (error) {
      if (error.status === 422) {
        this.setState({ error });
      } else {
        throw error;
      }
    }
  };

  handleSelectChange = value => {
    this.setState({ sendInvitation: value });
  };

  render() {
    const {
      classYearOptions,
      companyOptions,
      departmentOptions,
      labels,
      locationOptions,
      programOptions,
      organization,
      degreeOptions
    } = this.props;
    const { error, imageProgress, open, redirectTo, sendInvitation } = this.state;
    const offset = -new Date().getTimezoneOffset();
    const buttonText = sendInvitation ? "Send Invite" : "Add User";

    if (redirectTo) {
      return <Redirect to={redirectTo} />;
    }

    return (
      <>
        <Button primary icon="person-add" onClick={this.handleModalOpen}>
          <Trans>Add a New User</Trans>
        </Button>
        {open && (
          <ChqModalDialog className="ManageModal ManageUserModal" onClose={this.handleModalClose}>
            <ModalDialog.Heading onClose={this.handleModalClose}>
              <Trans>Add User</Trans>
            </ModalDialog.Heading>
            <ModalDialog.Body>
              {error && <ErrorMessage error={error} />}
              <Form
                onSubmit={this.handleSubmit}
                initialValues={{
                  locationName: locationOptions[0]?.value,
                  role: organization.roleAsDefault,
                  inviteNotificationCheck: true
                }}
              >
                <ImageField name="avatar" aspectRatio={1} progress={imageProgress}>
                  <Trans>Profile Photo</Trans>
                </ImageField>
                <EmailField name="email" required>
                  <Trans>E-mail Address *</Trans>
                </EmailField>
                {organization.firstNameLastNameEnabled ? (
                  <>
                    <StringField name="firstName" required>
                      <Trans>Invitee First Name *</Trans>
                    </StringField>
                    <StringField name="lastName" required>
                      <Trans>Invitee Last Name *</Trans>
                    </StringField>
                  </>
                ) : (
                  <StringField name="name" required>
                    <Trans>Invitee Name *</Trans>
                  </StringField>
                )}
                {!organization.isEducational && (
                  <StringField name="title">
                    <Trans>Invitee Title</Trans>
                  </StringField>
                )}
                <SelectField name="locationName" creatable options={locationOptions} required>
                  <Trans>Location *</Trans>
                </SelectField>
                {!organization.isEducational && (
                  <SelectField name="departmentNames" creatable multiple options={departmentOptions}>
                    <Trans>{labels.departments.multipleField}</Trans>
                  </SelectField>
                )}
                {canViewCompaniesField(organization) && (
                  <SelectField name="companyNames" creatable multiple options={companyOptions}>
                    <Trans>{labels.companies.multipleField}</Trans>
                  </SelectField>
                )}
                {!organization.isEducational && (
                  <DateTimeField name="startedOn" offset={offset}>
                    <Trans>Start date at the company</Trans>
                  </DateTimeField>
                )}
                {organization.isEducational && (
                  <>
                    <SelectField name="degreeName" creatable options={degreeOptions}>
                      <Trans>Degree</Trans>
                    </SelectField>
                    <SelectField name="programNames" creatable multiple options={programOptions}>
                      <Trans>Programs</Trans>
                    </SelectField>
                    <SelectField name="classYearName" creatable options={classYearOptions}>
                      <Trans>{organization.name === "Dartmouth Guarini" ? "Years in Program" : "Class year"}</Trans>
                    </SelectField>
                  </>
                )}
                <SelectField
                  name="role"
                  options={roles}
                  required
                >
                  <Trans>Account Role</Trans>
                </SelectField>
                <SelectField name="inviteNotificationCheck" options={FLAG_OPTIONS} onChange={this.handleSelectChange}>
                  <Trans>Do you want to send the invitation immediately?</Trans>
                </SelectField>
                <SubmitButton primary>
                  {submitting => (submitting ? t("Submitting...") : t(buttonText))}
                </SubmitButton>
              </Form>
            </ModalDialog.Body>
          </ChqModalDialog>
        )}
      </>
    );
  }
}

const InviteModal = () => {
  const { profile: { organization } } = useProfile();
  const {
    classYearOptions,
    companyOptions,
    departmentOptions,
    locationOptions,
    programOptions,
    degreeOptions
  } = useUserSelectOptions();

  const labels = useFieldsLabels();

  return (
    <InviteModalContext
      classYearOptions={classYearOptions}
      companyOptions={companyOptions}
      departmentOptions={departmentOptions}
      labels={labels}
      locationOptions={locationOptions}
      organization={organization}
      programOptions={programOptions}
      degreeOptions={degreeOptions}
    />
  );
};

export default InviteModal;
